import React from 'react';
import { ShoppingCart, User, Heart, Search, Crown, Star, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template16 = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-gray-50 font-serif">
            {/* Luxury Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template16')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            PURE STYLE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template16/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template16/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template16')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template16/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template16/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template16/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Elegant Hero */}
            <section className="relative h-[80vh] lg:h-screen">
                <img
                    src="https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=1920&h=1080&fit=crop"
                    alt="Luxury Fashion"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white max-w-4xl px-4">
                        <h2 className="text-5xl lg:text-8xl font-light mb-6 tracking-wider">Timeless</h2>
                        <h3 className="text-3xl lg:text-5xl font-bold mb-8 tracking-wide">ELEGANCE</h3>
                        <p className="text-lg lg:text-xl font-light mb-10 max-w-2xl mx-auto leading-relaxed">
                            Discover our exclusive collection of luxury fashion crafted for the discerning individual
                        </p>
                        <button className="bg-white text-black px-10 lg:px-16 py-4 lg:py-5 text-sm lg:text-base tracking-widest hover:bg-gray-100 transition-all duration-500 font-medium">
                            EXPLORE COLLECTION
                        </button>
                    </div>
                </div>
            </section>

            {/* Luxury Statement */}
            <section className="bg-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-12 text-center">
                    <Crown className="mx-auto mb-8 text-yellow-600" size={48} />
                    <h2 className="text-3xl lg:text-5xl font-light text-black mb-6 tracking-wide">Heritage of Excellence</h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        For over three decades, we have been crafting exceptional pieces that embody sophistication,
                        quality, and timeless style. Each piece tells a story of uncompromising craftsmanship.
                    </p>
                </div>
            </section>

            {/* Luxury Product Grid */}
            <section className="bg-gray-50 py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-light text-black mb-4 tracking-wide">Signature Collection</h2>
                        <div className="w-24 h-px bg-black mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                        {[1, 2, 3, 4, 5, 6].map(id => (
                            <div key={id} className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-700">
                                <div className="aspect-[3/4] overflow-hidden relative">
                                    <img
                                        src={`https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400&h=500&fit=crop&q=90&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=90&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${id * 17 + 200}`}
                                        alt={`Luxury Item ${id}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                    />
                                    <div className="absolute top-4 left-4">
                                        <ShieldCheck className="text-white bg-black/50 p-1 rounded" size={28} />
                                    </div>
                                </div>
                                <div className="p-6 lg:p-8">
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="text-xl font-medium text-black tracking-wide">Signature Piece {id}</h3>
                                        <div className="flex">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-500 mb-4 tracking-wide">Premium Collection</p>
                                    <div className="flex justify-between items-center">
                                        <span className="text-2xl font-light text-black">${(299 + id * 50)}.00</span>
                                        <button className="text-sm tracking-widest text-black border border-black px-6 py-2 hover:bg-black hover:text-white transition-all duration-300">
                                            SELECT
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Luxury Services */}
            <section className="bg-black py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-light text-white mb-6 tracking-wide">Prestige Services</h2>
                        <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                            Experience our exclusive services designed for our valued clientele
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: "Personal Styling", desc: "One-on-one consultation" },
                            { title: "Custom Tailoring", desc: "Bespoke alterations" },
                            { title: "VIP Access", desc: "Exclusive collections" }
                        ].map((service, index) => (
                            <div key={index} className="text-center p-8 border border-gray-800 hover:border-white transition-colors duration-500">
                                <Crown className="mx-auto mb-4 text-yellow-600" size={36} />
                                <h3 className="text-xl font-medium text-white mb-2 tracking-wide">{service.title}</h3>
                                <p className="text-gray-400 text-sm tracking-wide">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">PURE STYLE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template16')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template16/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template16/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template16/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@purestyle.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 PURE STYLE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template16;